import { useEffect, useRef } from "react";

// Pure waveform shape functions (phase 0..1 per cycle). Extracted to module
// scope so the animation effect stays small and these stay easy to reason about.
const ecgValue = (p) => {
  if (p < 0.08) return Math.sin((p / 0.08) * Math.PI) * 0.12; // P
  if (p < 0.16) return 0;
  if (p < 0.18) return -0.15; // Q
  if (p < 0.22) return 1.0; // R
  if (p < 0.26) return -0.35; // S
  if (p < 0.34) return 0;
  if (p < 0.5) return Math.sin(((p - 0.34) / 0.16) * Math.PI) * 0.25; // T
  return 0;
};

const spo2Value = (p) => {
  if (p < 0.15) return Math.sin((p / 0.15) * (Math.PI / 2)); // rise
  if (p < 0.5) return Math.cos(((p - 0.15) / 0.35) * (Math.PI / 2)) * 0.95; // fall
  if (p < 0.6) return 0.05 + Math.sin(((p - 0.5) / 0.1) * Math.PI) * 0.15; // notch
  return Math.max(0, 0.2 - (p - 0.6) * 0.4);
};

const respValue = (p) => Math.sin(p * 2 * Math.PI);

const capnoValue = (p) => {
  if (p < 0.05) return -0.9; // baseline (inspiration)
  if (p < 0.12) return -0.9 + ((p - 0.05) / 0.07) * 1.6; // rapid upstroke
  if (p < 0.45) return 0.7 + (p - 0.12) * 0.5; // alveolar plateau (slight rise)
  if (p < 0.5) return 0.87 - ((p - 0.45) / 0.05) * 1.77; // rapid downstroke
  return -0.9; // expiratory baseline
};

const WAVE_FNS = { ecg: ecgValue, spo2: spo2Value, resp: respValue, capno: capnoValue };

// Generic sweeping waveform component for vital signs.
// kind: "ecg" | "spo2" | "resp" | "capno"
// rateRef: ref returning current rate (HR for ecg, RR for resp, HR for SpO2 pleth)
// amplitudeRef: ref returning 0..1 amplitude scaling (used for hypoxemia/apnoea)
export default function Waveform({
  kind,
  color,
  height = 110,
  rateRef,
  amplitudeRef,
  background = "#050505",
  gridColor = "#1a1a1a",
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const stateRef = useRef({ x: 0, lastTs: 0, phase: 0, lastBeat: 0, points: [] });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      stateRef.current.points = new Array(w).fill(null);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const drawGrid = (w, h) => {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 1;
      const step = 20;
      ctx.beginPath();
      for (let x = 0; x <= w; x += step) {
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, h);
      }
      for (let y = 0; y <= h; y += step) {
        ctx.moveTo(0, y + 0.5);
        ctx.lineTo(w, y + 0.5);
      }
      ctx.stroke();
    };

    const waveFn = WAVE_FNS[kind];
    let lastTs = performance.now();
    let beatPhase = 0;
    const SWEEP_SPEED = 110; // px per second

    const tick = (ts) => {
      const dt = Math.min(0.05, (ts - lastTs) / 1000);
      lastTs = ts;

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const mid = h / 2;
      const rate = Math.max(0.01, rateRef.current ?? 60);
      const amp = amplitudeRef?.current ?? 1;

      const cyclesPerSec = rate / 60;
      beatPhase += cyclesPerSec * dt;
      if (beatPhase >= 1) beatPhase -= Math.floor(beatPhase);

      const prevX = stateRef.current.x;
      const newX = prevX + SWEEP_SPEED * dt;
      const startX = Math.floor(prevX);
      const endX = Math.floor(newX);
      const pts = stateRef.current.points;

      for (let x = startX; x <= endX; x++) {
        const xi = ((x % w) + w) % w;
        const cyclesPerPx = cyclesPerSec / SWEEP_SPEED;
        const localPhase = (beatPhase - (endX - x) * cyclesPerPx + 1000) % 1;
        let v = waveFn ? waveFn(localPhase) * amp : 0;
        v += (Math.random() - 0.5) * 0.015; // minor noise
        pts[xi] = v;
      }
      stateRef.current.x = newX % w;

      // erase a small gap ahead of cursor
      const eraseWidth = 30;
      for (let i = 0; i < eraseWidth; i++) {
        const ex = (Math.floor(stateRef.current.x) + i) % w;
        pts[ex] = null;
      }

      drawGrid(w, h);

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.shadowColor = color;
      ctx.shadowBlur = 6;
      ctx.beginPath();
      let pen = false;
      const yScale = h * 0.38;
      for (let x = 0; x < w; x++) {
        const v = pts[x];
        if (v === null || v === undefined) {
          pen = false;
          continue;
        }
        const y = mid - v * yScale;
        if (!pen) {
          ctx.moveTo(x + 0.5, y);
          pen = true;
        } else {
          ctx.lineTo(x + 0.5, y);
        }
      }
      ctx.stroke();
      ctx.shadowBlur = 0;

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  // Re-init only when identity/styling props change; refs are stable per render.
  }, [kind, color, background, gridColor, rateRef, amplitudeRef]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: "100%", height: `${height}px`, display: "block" }}
      data-testid={`waveform-${kind}`}
    />
  );
}
